// Application shells (layouts chosen per route in app/App.tsx):
//   StoreShell    — sidebar + top bar (store switcher, ⌘K, notifications, help, user menu, View store) + banners
//   PlatformShell — same frame with platformNav, an accent strip and "Back to my stores"
//   FullShell     — full-screen tools (editor, bulk editor): no sidebar; the page owns its top bar
//   BareShell     — logged in without store chrome (store picker, onboarding); `raw` routes (/_kit) pass through
//   PublicShell   — logged out: centered card with the brand mark (login, signup, reset, invite); on lg+ a split
//                   layout with a brand-story panel on the left and the form on the right
//   PageFallback  — page-shaped skeleton inside a shell; app-shaped skeleton while the session loads
// Every shell mounts the global providers (tooltips, toasts, confirm + reauth dialogs, theme sync).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { ArrowLeft, Banknote, ExternalLink, KeyRound, Menu, Monitor, Moon, Percent, ShieldCheck, ShoppingBag, Sparkles, Sun, UserCheck, Users, X, type LucideIcon } from 'lucide-react'
import { storeNav, platformNav } from '@/app/nav'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/toast'
import { ConfirmHost, Sheet } from '@/components/ui/dialog'
import { ReauthHost, installAuthExpiryGuard } from '@/components/ui/reauth'
import { Button, IconButton } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/feedback'
import { Badge } from '@/components/ui/badge'
import { SegmentedControl } from '@/components/ui/tabs'
import { cn } from '@/components/ui/cn'
import { useSession } from '@/lib/session'
import { installThemeSync, setThemePreference, useTheme, type ThemePreference } from '@/lib/theme'
import { setFormatDefaults } from '@/lib/format'
import { setTitleContext, useIsMobile, useLocalStorage, useStoreEvents } from '@/lib/hooks'
import { BrandLockup, BrandMark } from './icons'
import { Sidebar, PlanCard } from './sidebar'
import { StoreSwitcher, SearchTrigger, NotificationsBell, UserMenu, HelpMenu, useLogout } from './topbar-parts'
import { CommandPalette } from './command-palette'
import { ShortcutsDialog, useShellHotkeys } from './shortcuts'
import { ShellBanners } from './banners'
import { ShellContext, useShell, useRecentTracker, type ShellApi, type ShellKind } from './shell-context'
import { matchRoute } from './route-meta'

export { useShell } from './shell-context'
export { useShellBanner, announcePlanLimit } from './banners'

// ─── providers ──────────────────────────────────────────────────────────────────────────

/** True below ShellProviders: a page that renders <PublicShell> inside the 'raw' layout must not mount a second Toaster/ConfirmHost. */
const ProvidersMounted = createContext(false)

function ShellProviders({ children }: { children: ReactNode }) {
  const nested = useContext(ProvidersMounted)
  useEffect(() => { if (nested) return; installAuthExpiryGuard(); return installThemeSync() }, [nested])
  if (nested) return <>{children}</>
  return (
    <ProvidersMounted.Provider value>
      <TooltipProvider>
        {children}
        <Toaster />
        <ConfirmHost />
        <ReauthHost />
      </TooltipProvider>
    </ProvidersMounted.Provider>
  )
}

/** Keeps document.title and lib/format defaults in step with the route and store. */
function useShellMeta(kind: ShellKind) {
  const { store } = useSession()
  const { pathname } = useLocation()
  if (store && (kind === 'store' || kind === 'full')) setFormatDefaults({ currency: store.currency, locale: store.locale, timeZone: store.timezone })
  useEffect(() => {
    setTitleContext({
      routeTitle: matchRoute(pathname)?.title ?? null,
      storeName: kind === 'platform' ? 'Platform' : kind === 'store' || kind === 'full' ? store?.name ?? null : null,
      suffix: 'Admin',
    })
  }, [pathname, store?.name, kind])
}

// ─── the framed shells ──────────────────────────────────────────────────────────────────

function useFrameState(kind: ShellKind) {
  const [collapsed, setCollapsed] = useLocalStorage<boolean>('sidebar-collapsed', false, (v): v is boolean => typeof v === 'boolean')
  const [mobileNav, setMobileNav] = useState(false)
  const [palette, setPalette] = useState<{ open: boolean; page: 'root' | 'stores' | 'theme' }>({ open: false, page: 'root' })
  const [shortcuts, setShortcuts] = useState(false)
  const openPalette = useCallback((page: 'root' | 'stores' | 'theme' = 'root') => setPalette(p => (p.open && page === 'root' ? { open: false, page } : { open: true, page })), [])
  const openShortcuts = useCallback(() => setShortcuts(true), [])
  const toggleSidebar = useCallback(() => setCollapsed(c => !c), [setCollapsed])
  const api = useMemo<ShellApi>(() => ({ kind, openPalette, openShortcuts, toggleSidebar, sidebarCollapsed: collapsed, openMobileNav: () => setMobileNav(true) }), [kind, openPalette, openShortcuts, toggleSidebar, collapsed])
  return { api, collapsed, mobileNav, setMobileNav, palette, setPalette, shortcuts, setShortcuts }
}

function Frame({ kind, children }: { kind: 'store' | 'platform'; children: ReactNode }) {
  const { store, storeId, me } = useSession()
  const { pathname } = useLocation()
  const mobile = useIsMobile()
  const s = useFrameState(kind)
  const logout = useLogout()
  const mainRef = useRef<HTMLElement>(null)
  useShellMeta(kind)
  useStoreEvents(undefined, { enabled: kind === 'store' && !!storeId })
  useRecentTracker(kind === 'platform' ? 'platform' : storeId)
  useShellHotkeys({ kind, openPalette: s.api.openPalette, openShortcuts: s.api.openShortcuts, toggleSidebar: mobile ? undefined : s.api.toggleSidebar })

  // New page: close the drawer, scroll the content area to the top.
  useEffect(() => { s.setMobileNav(false); mainRef.current?.scrollTo({ top: 0 }) }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps
  const sidebarW = mobile ? 0 : s.collapsed ? 56 : 240
  useEffect(() => {
    document.documentElement.style.setProperty('--shell-sidebar-w', `${sidebarW}px`)
    return () => { document.documentElement.style.removeProperty('--shell-sidebar-w') }
  }, [sidebarW])

  const nav = kind === 'platform' ? platformNav : storeNav
  return (
    <ShellContext.Provider value={s.api}>
      <ShellProviders>
        <a href="#main" className="sr-only z-[80] rounded-control bg-surface px-3 py-2 text-base font-medium shadow-popover focus:not-sr-only focus:fixed focus:top-2 focus:left-2">Skip to content</a>
        <div className="flex h-dvh flex-col bg-chrome" style={{ '--shell-sidebar-w': `${sidebarW}px` } as CSSProperties}>
          {kind === 'platform' && <div aria-hidden className="h-[3px] shrink-0 bg-gradient-to-r from-platform via-accent to-primary" />}
          <header className="flex h-[52px] shrink-0 items-center gap-2 px-2 sm:px-3">
            {mobile ? (
              <>
                <IconButton icon={<Menu className="size-[18px]!" />} label="Open navigation" onClick={() => s.setMobileNav(true)} />
                {kind === 'store' ? <StoreSwitcher compact /> : <PlatformTitle />}
              </>
            ) : (
              <div className={cn('flex shrink-0 items-center gap-2 transition-[width] duration-180', s.collapsed ? 'w-auto pr-4' : 'w-[228px]')}>
                <Link to={kind === 'platform' ? '/platform' : '/'} aria-label="Home" className="grid size-8 shrink-0 place-items-center rounded-control focus-visible:outline-2 focus-visible:outline-focus"><BrandMark size={24} /></Link>
                {kind === 'store' ? <StoreSwitcher /> : <PlatformTitle />}
              </div>
            )}
            <div className="flex min-w-0 flex-1 justify-center">
              {mobile ? <span className="flex-1" /> : <SearchTrigger onOpen={() => s.api.openPalette()} placeholder={kind === 'platform' ? 'Jump to…' : 'Search or jump to…'} />}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {mobile && <SearchTrigger iconOnly onOpen={() => s.api.openPalette()} />}
              {kind === 'store' && store?.storefrontUrl && (
                <Button variant="ghost" asChild className="hidden text-text-muted hover:bg-chrome-hover sm:inline-flex" tooltip="Open your storefront in a new tab">
                  <a href={store.storefrontUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="size-4" /><span className="hidden xl:inline">View store</span></a>
                </Button>
              )}
              {kind === 'platform' && (
                <Button variant="ghost" asChild className="text-text-muted hover:bg-chrome-hover">
                  <Link to="/"><ArrowLeft className="size-4" /><span className="hidden sm:inline">Back to my stores</span></Link>
                </Button>
              )}
              {!mobile && <HelpMenu onShortcuts={s.api.openShortcuts} onPalette={() => s.api.openPalette()} />}
              {kind === 'store' && <NotificationsBell />}
              <div className="ml-1"><UserMenu onShortcuts={s.api.openShortcuts} /></div>
            </div>
          </header>
          <div className="flex min-h-0 flex-1">
            {!mobile && (
              <aside className="shrink-0">
                <Sidebar nav={nav} collapsed={s.collapsed} onToggleCollapsed={s.api.toggleSidebar} footer={kind === 'store' ? <PlanCard /> : undefined} />
              </aside>
            )}
            <main id="main" ref={mainRef} tabIndex={-1}
              className="relative min-w-0 flex-1 overflow-y-auto bg-background outline-none lg:rounded-tl-[14px] lg:border-t lg:border-l lg:border-border">
              <ShellBanners kind={kind} />
              {children}
            </main>
          </div>
        </div>
        {mobile && (
          <Sheet bare side="left" open={s.mobileNav} onOpenChange={s.setMobileNav} title="Navigation" className="bg-chrome">
            <Sidebar drawer nav={nav} onNavigate={() => s.setMobileNav(false)} footer={kind === 'store' ? <PlanCard /> : undefined}
              header={<div className="flex h-[52px] items-center gap-2 border-b border-border pr-2 pl-4"><BrandMark size={24} /><span className="min-w-0 flex-1 truncate text-base font-semibold">{kind === 'platform' ? 'Platform admin' : store?.name ?? me?.user.name}</span><IconButton icon={<X />} label="Close navigation" noTooltip onClick={() => s.setMobileNav(false)} /></div>} />
          </Sheet>
        )}
        <CommandPalette open={s.palette.open} initialPage={s.palette.page} onOpenChange={o => s.setPalette(p => ({ ...p, open: o }))} kind={kind}
          onShowShortcuts={s.api.openShortcuts} onLogout={() => void logout()} />
        <ShortcutsDialog open={s.shortcuts} onOpenChange={s.setShortcuts} />
      </ShellProviders>
    </ShellContext.Provider>
  )
}

function PlatformTitle() {
  return (
    <span className="flex min-w-0 items-center gap-2 px-1">
      <span className="truncate text-base font-semibold text-text">Platform</span>
      <Badge tone="warning" size="sm">Admin</Badge>
    </span>
  )
}

export const StoreShell = ({ children }: { children: ReactNode }) => <Frame kind="store">{children}</Frame>
export const PlatformShell = ({ children }: { children: ReactNode }) => <Frame kind="platform">{children}</Frame>

/** Full-screen tools (theme editor, bulk editor): the page renders its own top bar; ⌘K and shortcuts still work. */
export function FullShell({ children }: { children: ReactNode }) {
  const { storeId } = useSession()
  const s = useFrameState('full')
  const logout = useLogout()
  useShellMeta('full')
  useStoreEvents(undefined, { enabled: !!storeId })
  useRecentTracker(storeId)
  useShellHotkeys({ kind: 'full', openPalette: s.api.openPalette, openShortcuts: s.api.openShortcuts })
  return (
    <ShellContext.Provider value={s.api}>
      <ShellProviders>
        <div className="flex h-dvh flex-col bg-background">
          <ShellBanners kind="full" />
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </div>
        <CommandPalette open={s.palette.open} initialPage={s.palette.page} onOpenChange={o => s.setPalette(p => ({ ...p, open: o }))} kind="full"
          onShowShortcuts={s.api.openShortcuts} onLogout={() => void logout()} />
        <ShortcutsDialog open={s.shortcuts} onOpenChange={s.setShortcuts} />
      </ShellProviders>
    </ShellContext.Provider>
  )
}

// ─── bare / public ──────────────────────────────────────────────────────────────────────

function ThemeSwitch() {
  const { preference } = useTheme()
  return (
    <SegmentedControl<ThemePreference> size="sm" aria-label="Theme" value={preference} onValueChange={setThemePreference}
      options={[{ value: 'light', label: '', icon: <Sun />, 'aria-label': 'Light' }, { value: 'dark', label: '', icon: <Moon />, 'aria-label': 'Dark' }, { value: 'system', label: '', icon: <Monitor />, 'aria-label': 'System' }]} />
  )
}

/** Logged in, no store chrome (store picker, onboarding). Routes with layout 'raw' (/_kit) render untouched. */
export function BareShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const raw = matchRoute(pathname)?.layout === 'raw'
  const { me } = useSession()
  const s = useFrameState('bare')
  useShellMeta(raw ? 'raw' : 'bare')
  if (raw) return <ShellProviders>{children}</ShellProviders>
  return (
    <ShellContext.Provider value={s.api}>
      <ShellProviders>
        <div className="flex min-h-dvh flex-col bg-background">
          <header className="flex h-14 shrink-0 items-center justify-between gap-3 px-4 sm:px-6">
            <Link to={me?.stores.length ? '/' : '/stores'} aria-label="Home" className="rounded-control focus-visible:outline-2 focus-visible:outline-focus"><BrandLockup /></Link>
            <div className="flex items-center gap-2">
              {me && me.stores.length > 0 && pathname !== '/' && <Button variant="ghost" asChild className="hidden sm:inline-flex"><Link to="/"><ArrowLeft className="size-4" />Back to store</Link></Button>}
              <UserMenu onShortcuts={s.api.openShortcuts} showStores={pathname !== '/stores'} />
            </div>
          </header>
          <main id="main" className="mx-auto flex w-full max-w-5xl flex-1 flex-col px-4 pt-4 pb-16 sm:px-6 sm:pt-8">{children}</main>
        </div>
        <ShortcutsDialog open={s.shortcuts} onOpenChange={s.setShortcuts} />
      </ShellProviders>
    </ShellContext.Provider>
  )
}

/**
 * Logged out. Renders the page INSIDE a centered card: pages supply only the card content
 * (heading, form). Use <AuthHeader title description /> for the standard heading.
 * lg+ (≥1024 px): split layout — a brand-story panel (copy chosen by route) on the left, the form
 * on the right without the card chrome. A page in the 'raw' layout (/invite/:token, which must work
 * signed in or out) may render <PublicShell> itself; it then reuses the outer providers.
 */
export function PublicShell({ children }: { children: ReactNode }) {
  useShellMeta('public')
  const { pathname } = useLocation()
  const story = storyFor(pathname)
  return (
    <ShellProviders>
      <div className="min-h-dvh bg-background lg:grid lg:grid-cols-[minmax(440px,5fr)_7fr]">
        <BrandStory story={story} />
        <div className="relative flex min-h-dvh min-w-0 flex-col items-center overflow-hidden px-4 pt-[max(40px,8vh)] pb-8 lg:justify-center lg:overflow-visible lg:pt-10">
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(60%_60%_at_50%_0%,color-mix(in_oklab,var(--primary)_14%,transparent),transparent)] lg:hidden" />
          <Link to="/login" aria-label="Storix" className="relative rounded-control focus-visible:outline-2 focus-visible:outline-focus lg:hidden"><BrandLockup /></Link>
          <p className="relative mt-3 max-w-[340px] text-center text-sm text-text-muted lg:hidden">{story.mobileLine}</p>
          <main id="main" className="relative mt-6 w-full max-w-[420px] rounded-dialog border border-border bg-surface p-6 shadow-card sm:p-8 lg:my-auto lg:max-w-[400px] lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none">{children}</main>
          <footer className="relative mt-auto flex items-center gap-4 pt-10 text-sm text-text-subtle lg:mt-0 lg:w-full lg:max-w-[400px] lg:justify-between lg:pt-8">
            <span>© {new Date().getFullYear()} Storix</span>
            <ThemeSwitch />
          </footer>
        </div>
      </div>
    </ShellProviders>
  )
}

// ─── brand story (PublicShell, lg+) ─────────────────────────────────────────────────────

interface Story { eyebrow: string; title: string; lead: string; mobileLine: string; points: Array<{ icon: LucideIcon; title: string; text: string }> }

const SELL_POINTS: Story['points'] = [
  { icon: Percent, title: '0% platform fee', text: 'On Stripe, PayPal, Razorpay, UPI and every other gateway — on every plan.' },
  { icon: Sparkles, title: 'An AI that builds with you', text: 'Describe your business; get a store, products and copy you can edit.' },
  { icon: ShoppingBag, title: 'Checkout you can shape', text: 'A fast one-page checkout you can edit on every plan, not just the top one.' },
  { icon: Banknote, title: 'Paid the way buyers pay', text: 'Cash on delivery, UPI QR, cards and local currencies built in.' },
]

function storyFor(pathname: string): Story {
  if (pathname.startsWith('/signup')) return {
    eyebrow: 'Start free · no card needed', title: 'Launch a store that keeps every sale.', mobileLine: 'Start free — 0% platform fee on every plan.',
    lead: 'Reviews, wishlists, discounts, abandoned-cart emails and custom domains come built in — not as paid add-ons.',
    points: SELL_POINTS,
  }
  if (pathname.startsWith('/forgot')) return {
    eyebrow: 'Account recovery', title: 'Back into your store in a minute.', mobileLine: 'Codes expire after 10 minutes.',
    lead: 'We email a 6-digit code to the address on your account. Nothing changes until you choose a new password.',
    points: [
      { icon: KeyRound, title: 'Short-lived codes', text: 'Each code works once and expires after 10 minutes.' },
      { icon: ShieldCheck, title: 'Every other device signed out', text: 'Resetting your password ends all other sessions straight away.' },
      { icon: UserCheck, title: 'No password? No problem', text: 'You can always log in with an emailed code instead.' },
    ],
  }
  if (pathname.startsWith('/invite')) return {
    eyebrow: 'Team invitation', title: 'Run the store together.', mobileLine: 'Staff only see what they’re given access to.',
    lead: 'Staff accounts get exactly the access the owner gives them — orders, products, customers or more.',
    points: [
      { icon: Users, title: 'Your own login', text: 'Every teammate signs in with their own email; nothing is shared.' },
      { icon: ShieldCheck, title: 'Permission by permission', text: 'Owners and admins decide what each person can see and change.' },
      { icon: Sparkles, title: 'The same fast admin', text: '⌘K search, keyboard shortcuts and live order updates.' },
    ],
  }
  return {
    eyebrow: 'Welcome back', title: 'Your store, right where you left it.', mobileLine: '0% platform fee on every plan.',
    lead: 'Orders, payouts, customers and your AI assistant — one calm, fast admin.',
    points: SELL_POINTS,
  }
}

/** The left panel on lg+: a fixed dark brand surface in both themes (like a printed cover), with an illustrative product glimpse. */
function BrandStory({ story }: { story: Story }) {
  return (
    <aside aria-label="About Storix"
      className="relative hidden overflow-hidden bg-[#150f3a] text-white lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col lg:justify-between lg:gap-8 lg:px-12 lg:py-10 xl:px-16 dark:bg-[#0d0a24]">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_55%_at_15%_0%,rgba(124,108,246,0.55),transparent_70%),radial-gradient(55%_45%_at_100%_100%,rgba(214,95,228,0.32),transparent_70%)]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,0.9)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.9)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(80%_70%_at_30%_30%,black,transparent)]" />
      <Link to="/login" aria-label="Storix home" className="relative inline-flex w-fit items-center gap-2 rounded-control focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
        <PanelMark /><span className="text-lg font-semibold tracking-[-0.02em]">Storix</span>
      </Link>
      <div className="relative flex max-w-[520px] flex-col gap-4">
        <span className="w-fit rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white/85 ring-1 ring-white/15">{story.eyebrow}</span>
        <h2 className="text-[34px] leading-[1.12] font-semibold tracking-[-0.025em] text-balance xl:text-[40px]">{story.title}</h2>
        <p className="text-md leading-relaxed text-white/72">{story.lead}</p>
        <ProductGlimpse />
      </div>
      <ul className="relative grid max-w-[560px] gap-x-8 gap-y-5 xl:grid-cols-2">
        {story.points.map(p => (
          <li key={p.title} className="flex gap-3">
            <span className="grid size-8 shrink-0 place-items-center rounded-control bg-white/10 ring-1 ring-white/15"><p.icon className="size-4 text-white" aria-hidden /></span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-base font-semibold text-white">{p.title}</span>
              <span className="text-sm leading-snug text-white/65">{p.text}</span>
            </span>
          </li>
        ))}
      </ul>
    </aside>
  )
}

/**
 * The brand mark for the panel. Not <BrandMark>: its gradient id would be defined first inside this
 * panel, which is display:none below lg — and a gradient in an undisplayed subtree paints nothing,
 * blanking every other BrandMark on the page (the mobile lockup).
 */
function PanelMark() {
  return (
    <svg width={28} height={28} viewBox="0 0 32 32" aria-hidden>
      <rect width="32" height="32" rx="9" fill="#6d5df0" />
      <path d="M21.5 11.2c-.9-1.6-2.9-2.7-5.4-2.7-3.3 0-5.6 1.8-5.6 4.3 0 2.4 1.9 3.5 5 4.1l1.6.3c1.9.4 2.7.9 2.7 1.9 0 1.2-1.3 2-3.3 2-2 0-3.5-.8-4.1-2.3"
        fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
      <circle cx="21.6" cy="22.4" r="1.7" fill="#fff" fillOpacity=".85" />
    </svg>
  )
}

/** Decorative: what a merchant sees on a good day (a sales tile and a new-order toast). Hidden on short screens. */
function ProductGlimpse() {
  return (
    <div aria-hidden className="relative mt-6 hidden h-[190px] select-none [@media(min-height:820px)]:block">
      <div className="absolute top-0 left-0 w-[290px] rounded-[14px] bg-white/[0.08] p-4 shadow-[0_24px_48px_-16px_rgba(0,0,0,0.5)] ring-1 ring-white/15 backdrop-blur-sm">
        <div className="flex items-center justify-between text-sm text-white/65">
          <span>Sales today</span>
          <span className="rounded-full bg-[#3ec27a]/20 px-2 py-0.5 text-xs font-semibold text-[#7ee2a8]">+18.4%</span>
        </div>
        <div className="mt-1 text-[26px] font-semibold tracking-[-0.02em] tabular-nums">₹48,210</div>
        <svg viewBox="0 0 258 48" className="mt-2 h-12 w-full" fill="none">
          <defs><linearGradient id="bs-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#aba2ff" stopOpacity=".45" /><stop offset="1" stopColor="#aba2ff" stopOpacity="0" /></linearGradient></defs>
          <path d="M0 40 L22 36 L44 38 L66 30 L88 32 L110 24 L132 27 L154 18 L176 21 L198 12 L220 15 L242 6 L258 8 L258 48 L0 48 Z" fill="url(#bs-fill)" />
          <path d="M0 40 L22 36 L44 38 L66 30 L88 32 L110 24 L132 27 L154 18 L176 21 L198 12 L220 15 L242 6 L258 8" stroke="#c4bdff" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
      </div>
      <div className="absolute top-[96px] left-[250px] flex w-[250px] items-center gap-3 rounded-[12px] bg-white p-3 text-[#17171c] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.55)]">
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[#e7f6ec] text-[#15693a]"><ShoppingBag className="size-4" /></span>
        <span className="flex min-w-0 flex-col">
          <span className="text-sm font-semibold">New order #1042</span>
          <span className="truncate text-xs text-[#5b5b67]">UPI · ₹2,499 · just now</span>
        </span>
      </div>
    </div>
  )
}

/** Standard heading for PublicShell pages. */
export function AuthHeader({ title, description }: { title: ReactNode; description?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-col gap-2 text-center">
      <h1 className="text-xl font-semibold tracking-[-0.012em] text-text">{title}</h1>
      {description && <p className="text-base text-text-muted">{description}</p>}
    </div>
  )
}

// ─── fallbacks ──────────────────────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-5 px-4 pt-5 sm:px-6 lg:px-8 lg:pt-7" role="status" aria-label="Loading page">
      <div className="flex items-center justify-between gap-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-8 w-28" rounded="md" />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {[0, 1, 2].map(i => <div key={i} className="flex flex-col gap-3 rounded-card border border-border bg-surface p-4 shadow-card"><Skeleton className="h-3 w-20" /><Skeleton className="h-6 w-28" /></div>)}
      </div>
      <div className="flex flex-col rounded-card border border-border bg-surface shadow-card">
        <div className="flex gap-3 border-b border-border p-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-8 w-20" /></div>
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="flex items-center gap-4 border-b border-border px-4 py-4 last:border-b-0">
            <Skeleton className="size-8" rounded="md" /><Skeleton className="h-3" style={{ width: `${[38, 30, 44, 26, 34, 40][i]}%` }} /><Skeleton className="ml-auto h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Loading placeholder. Inside a shell (lazy page loading) it's a page-shaped skeleton; before the
 * session has loaded it mirrors the whole app frame so nothing jumps when the shell appears.
 */
export function PageFallback() {
  const shell = useShell()
  const { pathname } = useLocation()
  if (shell) return shell.kind === 'bare' ? <div className="flex flex-col gap-4 py-6"><Skeleton className="h-6 w-56" /><Skeleton className="h-40 w-full" rounded="card" /></div> : <PageSkeleton />
  const layout = matchRoute(pathname)?.layout
  if (layout === 'store' || layout === 'platform') {
    return (
      <div className="flex h-dvh flex-col bg-chrome" aria-busy="true">
        <div className="flex h-[52px] shrink-0 items-center gap-3 px-3"><BrandMark size={24} /><Skeleton className="h-4 w-32" /><Skeleton className="mx-auto hidden h-8 w-[420px] md:block" rounded="md" /><Skeleton className="ml-auto size-7" rounded="full" /></div>
        <div className="flex min-h-0 flex-1">
          <div className="hidden w-60 shrink-0 flex-col gap-2 px-3 py-3 lg:flex">{Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-7" style={{ width: `${[70, 60, 75, 65, 55, 68, 50, 62][i]}%` }} />)}</div>
          <div className="min-w-0 flex-1 bg-background lg:rounded-tl-[14px] lg:border-t lg:border-l lg:border-border"><PageSkeleton /></div>
        </div>
      </div>
    )
  }
  return (
    <div className="grid min-h-dvh place-items-center bg-background" role="status" aria-label="Loading">
      <BrandMark size={32} className="animate-pulse" />
    </div>
  )
}
